/**
 * Display formatting for Sales View (§6.6). Pure, so the rules are testable
 * without mounting a card.
 */

/** Up/down/flat/new, plus the label to render. */
export interface Trend {
  direction: 'up' | 'down' | 'flat' | 'new';
  label: string;
}

const FLAT: Trend = { direction: 'flat', label: '—' };

/**
 * VOLUME trend: percent change, ±5% flat band. The Client View `getTrend` rule
 * (§6.3), reproduced rather than imported so Client View's contract stays fixed.
 *
 * Only valid for COUNTS. For rates use `getRateTrend` -- see the note there.
 */
export function getVolumeTrend(
  current: number | null | undefined,
  prior: number | null | undefined,
): Trend {
  if (current == null) return FLAT;
  if (prior == null || prior === 0) {
    return current > 0 ? { direction: 'new', label: 'New' } : FLAT;
  }
  const deltaPct = ((current - prior) / prior) * 100;
  if (deltaPct > 5) return { direction: 'up', label: `+${deltaPct.toFixed(1)}%` };
  if (deltaPct < -5) return { direction: 'down', label: `${deltaPct.toFixed(1)}%` };
  return { direction: 'flat', label: `${deltaPct.toFixed(1)}%` };
}

/** Flat band for rate moves, in percentage POINTS. */
export const RATE_FLAT_BAND_PTS = 1.0;

/** Below this many units in the comparison window, a percentage is noise: 1 -> 3.68 is "+268%". */
export const MIN_PCT_BASE_UNITS = 5;

const units = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(1));

/**
 * COUNT trend with the unit change beside the percent (CSE-5/6): "-50.0% (-3)".
 * On a base under `MIN_PCT_BASE_UNITS` the percent is dropped and the move is left
 * uncoloured ("+3 units"), so one car cannot read as an emergency.
 */
export function getCountTrend(
  current: number | null | undefined,
  prior: number | null | undefined,
): Trend {
  if (current == null) return FLAT;
  if (prior == null || prior === 0) {
    return current > 0 ? { direction: 'new', label: 'New' } : FLAT;
  }
  const delta = current - prior;
  const signed = `${delta > 0 ? '+' : delta < 0 ? '-' : ''}${units(Math.abs(delta))}`;
  if (Math.abs(prior) < MIN_PCT_BASE_UNITS) {
    return { direction: 'flat', label: `${signed} unit${Math.abs(delta) === 1 ? '' : 's'}` };
  }
  const pct = getVolumeTrend(current, prior);
  return { direction: pct.direction, label: `${pct.label} (${signed})` };
}

/**
 * RATE trend: percentage POINTS, never percent-change.
 *
 * §6.3 says to reuse `getTrend` for trend indicators, but §6.6 and
 * `SalesHeadlineMetrics` both require rates to move in points: a conversion going
 * 20% -> 10% is "down 10 pts", and rendering it as "-50%" reads as an emergency.
 * Percent-change on a rate is also unstable near zero -- 0.4% -> 0.2% is another
 * "-50%". So volumes use percent-change and rates use points.
 *
 * Takes RATIOS (0-1), the shape the API returns, and reports points.
 */
export function getRateTrend(
  current: number | null | undefined,
  prior: number | null | undefined,
): Trend {
  if (current == null || prior == null) return FLAT;
  const pts = (current - prior) * 100;
  if (pts > RATE_FLAT_BAND_PTS) return { direction: 'up', label: `+${pts.toFixed(1)} pts` };
  if (pts < -RATE_FLAT_BAND_PTS) return { direction: 'down', label: `${pts.toFixed(1)} pts` };
  return { direction: 'flat', label: `${pts.toFixed(1)} pts` };
}

/**
 * Ratio (0-1) as a percent. A zero or absent DENOMINATOR upstream arrives as
 * null, which renders "—"; a real zero renders "0.0%", never NaN (§6.6).
 */
export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

/** Threshold the Manheim Dealer Performance sheet marks red. Reps know this line. */
export const MMR_RED_THRESHOLD = 1.15;

/** True when a %MMR ratio should render red (>115%, §6.6). */
export function isMmrRed(ratio: number | null | undefined): boolean {
  return ratio != null && !Number.isNaN(ratio) && ratio > MMR_RED_THRESHOLD;
}

/** Enrolment flag as "Yes" or blank. Deliberately NOT "Yes"/"No": these flags come
 *  from a LEFT JOIN, so false and missing are indistinguishable downstream. A blank
 *  reads as "not on record"; a "No" would read as a verified negative it is not. */
export function yesBlank(flag: boolean | null | undefined): string {
  return flag ? 'Yes' : '—';
}

export interface AncillaryFlags {
  dealshield_active?: boolean | null;
  ds_select_active?: boolean | null;
  psi_active?: boolean | null;
  ds_psi_bundle_active?: boolean | null;
  cd_client?: boolean | null;
  ready_active_user?: boolean | null;
}

/** Every ancillary product with whether this client holds it, owned first so the
 *  footprint reads at a glance. One chip row replaces eight Yes/blank rows.
 *
 *  `ready_account` is EXCLUDED on purpose -- it is registration, true for ~99.5% of
 *  accounts, so it would appear on nearly every client while meaning nothing. */
export function productMatrix(a: AncillaryFlags): { label: string; owned: boolean }[] {
  const slots: { label: string; owned: boolean }[] = [
    { label: 'DealShield', owned: !!a.dealshield_active },
    { label: 'DS Select', owned: !!a.ds_select_active },
    { label: 'PSI', owned: !!a.psi_active },
    { label: 'DS/PSI bundle', owned: !!a.ds_psi_bundle_active },
    { label: 'Central Dispatch', owned: !!a.cd_client },
    { label: 'Ready Logistics', owned: !!a.ready_active_user },
  ];
  return [...slots.filter((s) => s.owned), ...slots.filter((s) => !s.owned)];
}

/** Volume-weighted ratio. Never average per-VIN ratios (§6.6). A zero or absent
 *  denominator yields null so the caller renders "—" rather than Infinity. */
export function weightedRatio(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null {
  if (numerator == null || denominator == null || denominator === 0) return null;
  return numerator / denominator;
}

export interface CoxProductFlags {
  has_atc?: boolean | null;
  has_vinsolutions?: boolean | null;
  has_dealercom?: boolean | null;
  has_dealertrack_fi?: boolean | null;
  has_dms?: boolean | null;
  has_ico?: boolean | null;
  has_stockwave?: boolean | null;
  ngc_client?: boolean | null;
}

/** The Cox Automotive software footprint, owned first. Same chip treatment as the
 *  ancillary matrix, and the same caveat with a harder edge: these flags are 'Y' or
 *  NULL with NO 'N' in the source, so a faded chip is strictly "not reported" —
 *  there is no such thing as a verified negative here. Never render one as "No".
 *
 *  No vAuto chip: the feed carries vAuto MEASURES (the `VAT_` columns) but no vAuto
 *  ownership flag, so claiming one either way would be invention. */
export function coxProductMatrix(m: CoxProductFlags): { label: string; owned: boolean }[] {
  const slots: { label: string; owned: boolean }[] = [
    { label: 'NextGear Capital', owned: !!m.ngc_client },
    { label: 'Autotrader', owned: !!m.has_atc },
    { label: 'VinSolutions', owned: !!m.has_vinsolutions },
    { label: 'Dealer.com', owned: !!m.has_dealercom },
    { label: 'Dealertrack F&I', owned: !!m.has_dealertrack_fi },
    { label: 'Dealertrack DMS', owned: !!m.has_dms },
    { label: 'Kelley Blue Book ICO', owned: !!m.has_ico },
    { label: 'Stockwave', owned: !!m.has_stockwave },
  ];
  return [...slots.filter((s) => s.owned), ...slots.filter((s) => !s.owned)];
}

/** A month-end date as "August 2026". The Cox Automotive performance block is
 *  monthly while every other number in the drawer is daily, so the label is not
 *  optional. */
export function formatMonthLabel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** Projected-full-month over last month, read as a pace. Bands are deliberately
 *  wide: this is a projection off a partial month, so calling a 4% shortfall
 *  "behind" would manufacture alarm the data cannot support. */
export function paceRead(
  pace: number | null | undefined,
  prior?: number | null,
): { direction: Trend['direction']; label: string } | null {
  if (pace == null || Number.isNaN(pace)) return null;
  const units = prior != null && prior > 0
    ? `on pace for ~${Math.round(pace * prior).toLocaleString()} vs ${prior.toLocaleString()}, `
    : '';
  const pct = `${units}${Math.round(pace * 100)}% of last month`;
  if (pace >= 1.05) return { direction: 'up', label: `Ahead · ${pct}` };
  if (pace >= 0.9) return { direction: 'flat', label: `On track · ${pct}` };
  return { direction: 'down', label: `Behind · ${pct}` };
}

/** Counts with thousands separators; "—" when absent, "0" when genuinely zero. */
export function formatCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

/** "340 vs 298 LY (+42)" — YTD against the same span last year, with the unit change (CSE-8). */
export function ytdVsLastYear(ytd: number | null | undefined, pytd: number | null | undefined): string {
  if (ytd == null || Number.isNaN(ytd)) return '—';
  if (pytd == null || Number.isNaN(pytd)) return formatCount(ytd);
  const delta = ytd - pytd;
  return `${formatCount(ytd)} vs ${formatCount(pytd)} LY (${delta >= 0 ? '+' : '-'}${formatCount(Math.abs(delta))})`;
}

/** One decimal for the small weekly averages, where "8" and "8.4" differ. */
export function formatAvg(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(1);
}

/** Compact age: "3d", "12d", "Never". Never-contacted is "Never", not "—" or 0 (§6.6). */
export function formatDaysAgo(days: number | null | undefined): string {
  if (days == null) return 'Never';
  if (days <= 0) return 'Today';
  return `${days}d`;
}

/** CSE-1c: the live-conversation half of contact recency, "41d since live". */
export function formatLiveContact(days: number | null | undefined): string {
  return days == null ? 'no live contact' : `${formatDaysAgo(days)} since live`;
}

/** CSE-1c: the in-person tier, "60d since visit". */
export function formatVisit(days: number | null | undefined): string {
  return days == null ? 'no visit' : `${formatDaysAgo(days)} since visit`;
}

/** "MMM D, YYYY", or "Never" when the client has never been contacted. */
export function formatContactDate(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  // A bare date is a calendar day: parsed as UTC midnight it renders as the day before in the US.
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * SFDC writes a literal '-' as its "no dealer group" placeholder. Render blank,
 * not a dash that reads like a real group name (§6.6).
 *
 * The Snowflake build already normalises this to NULL, so this is a second line of
 * defence for any path that reads the raw column.
 */
export function normalizeDealerGroup(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  return text === '-' ? '' : text;
}

/** "Day 4 of 7" for the week-in-progress caveat. */
export function weekDayLabel(elapsedFraction: number | null | undefined): string {
  if (!elapsedFraction || elapsedFraction <= 0) return '';
  return `day ${Math.round(elapsedFraction * 7)} of 7`;
}

/**
 * Week-to-date status worth showing.
 *
 * The card deliberately does NOT carry a "this week" volume column. Measured on
 * the live book: only 4.7% of clients have sold anything in the current
 * (partial) week and 12.9% in a full week, so a TW column would read 0.0 for
 * ~95% of rows and every one of them would look like a collapse.
 *
 * What IS worth surfacing is the exception -- a client already back on pace this
 * week. "No Activity Yet" is the default state for almost everyone, so it is
 * suppressed rather than repeated 95 times down the carousel.
 */
export function meaningfulWtdStatus(status: string | null | undefined): string | null {
  if (status === 'On Track' || status === 'Partial Recovery') return status;
  return null;
}

/** Share of the risk budget one signal contributed. `totalWeight` is REQUIRED:
 *  it has moved twice (27 -> 26 -> 30) and a default silently misreports every
 *  percentage on the breakdown when it next changes. */
export function contributionShare(contribution: number, totalWeight: number): number {
  if (!totalWeight) return 0;
  return contribution / totalWeight;
}

/** Short engagement-band label for the card, e.g. "At Risk - 60+ Days" -> "At Risk". */
export function shortEngagementBand(band: string | null | undefined): string {
  if (!band) return '';
  return band.split(' - ')[0];
}

/**
 * `health_score` from the API is a RISK score: 0 is a healthy client and the
 * worst client on the book scores highest (URGENT starts at 0.432; the observed
 * max is 0.613). Reps read a bare 0.350 as "35%, that's terrible" when it is
 * actually a mid-tier account.
 *
 * So the card shows HEALTH, inverted, where 100% is a clean bill: health = 1 - risk.
 * Sorting and tiering keep using the raw risk score untouched.
 */
export function healthPercent(risk: number | null | undefined): number | null {
  if (risk == null || Number.isNaN(risk)) return null;
  const clamped = Math.min(1, Math.max(0, risk));
  return Math.round((1 - clamped) * 100);
}

/** Health as a whole percent, or "—" when the score is absent. */
export function formatHealthPercent(risk: number | null | undefined): string {
  const pct = healthPercent(risk);
  return pct == null ? '—' : `${pct}%`;
}

/**
 * Deduplicated reason groups, worst first.
 *
 * `reasons` arrives sorted by weighted contribution, so taking groups in first
 * appearance order puts the biggest driver first. Falls back to the flat
 * `reason_groups` list when a client carries groups but no itemised reasons.
 */
export function reasonGroupsByImpact(
  reasons: { group: string }[],
  fallback: string[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of reasons) {
    if (r.group && !seen.has(r.group)) {
      seen.add(r.group);
      out.push(r.group);
    }
  }
  for (const g of fallback) {
    if (g && !seen.has(g)) {
      seen.add(g);
      out.push(g);
    }
  }
  return out;
}
